// src/components/answer/AnswerRenderer.jsx

import React, { useEffect, useLayoutEffect, useMemo } from "react";
import AnswerText from "./AnswerText";
import AnswerSources from "./AnswerSources";
import AnswerTable from "./AnswerTable";
import AnswerMath from "./AnswerMath";
import AnswerChart from "./AnswerChart";


// -----------------------------------------------------------------------------
// SINGLE-FILE STYLE OWNERSHIP
// -----------------------------------------------------------------------------
// AnswerRenderer can appear many times in one chat. Install the shared stylesheet
// once in <head> and reference-count mounted renderer instances so we do not emit
// one large <style> element per assistant message.
//
// The former AnswerRenderer.css content is preserved below in full.
const ANSWER_RENDERER_STYLE_ID = "asklawly-answer-renderer-styles";
const ANSWER_RENDERER_CSS = String.raw`/*
  AnswerRenderer.css

  Universal answer presentation styling for:
  - normal assistant answers
  - findings / explanations
  - citation markers
  - clean source labels
  - show 5 more sources
  - markdown tables rendered as real tables
  - polished math / finance / chemistry formula cards
  - robust chart / graph / diagram / geometry cards

  This file is intentionally scoped under .answer-renderer
  so it does not disturb the rest of ChatInterface.css.

  Updated for the upgraded AnswerChart.jsx / Recharts chart engine:
  - full-width charts
  - responsive Recharts containers
  - horizontal overflow safety for large visuals
  - better mobile behavior
  - better print behavior
*/

/* =========================
   Root
   ========================= */

.answer-renderer {
  width: 100%;
  max-width: 100%;
  color: #111827;
  font-size: 16px;
  line-height: 1.5;
  overflow-wrap: break-word;
  word-break: normal;
  box-sizing: border-box;
}

.answer-renderer *,
.answer-renderer *::before,
.answer-renderer *::after {
  box-sizing: border-box;
}

/* =========================
   Text
   ========================= */

.answer-text {
  width: 100%;
  max-width: 100%;
}

.answer-paragraph-group {
  margin: 0 0 10px;
}

.answer-paragraph {
  margin: 0 0 10px;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
}

.answer-paragraph:last-child {
  margin-bottom: 0;
}

.answer-section-label {
  margin: 14px 0 8px;
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.answer-list {
  margin: 8px 0 12px 20px;
  padding: 0;
  white-space: normal;
}

.answer-list li {
  margin: 5px 0;
  padding-left: 2px;
  overflow-wrap: break-word;
  word-break: normal;
}

.answer-numbered-list {
  margin-left: 22px;
}

.answer-citation-marker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: super;
  margin-left: 2px;
  font-size: 11px;
  line-height: 1;
  font-weight: 800;
  color: #0b5fff;
  cursor: default;
}

/* =========================
   Sources
   ========================= */

.answer-sources {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(15, 23, 42, 0.1);
}

.answer-sources-title {
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 850;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.answer-sources-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.answer-source-row {
  width: 100%;
}

.answer-source-link {
  display: inline-flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 100%;
  padding: 6px 9px;
  border-radius: 10px;
  background: rgba(239, 246, 255, 0.9);
  border: 1px solid rgba(59, 130, 246, 0.16);
  color: #0b5fff;
  text-decoration: none;
  font-size: 14px;
  line-height: 1.35;
  transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
}

.answer-source-link:hover {
  background: rgba(219, 234, 254, 0.95);
  border-color: rgba(37, 99, 235, 0.28);
  transform: translateY(-1px);
}

.answer-source-link:active {
  transform: translateY(0);
}

.answer-source-link-static {
  cursor: default;
}

.answer-source-number {
  flex: 0 0 auto;
  font-weight: 850;
  color: #1d4ed8;
}

.answer-source-label {
  min-width: 0;
  overflow-wrap: break-word;
  word-break: normal;
  color: #1e3a8a;
  font-weight: 650;
}

.answer-show-more-sources {
  margin-top: 10px;
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  color: #0f172a;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, transform 120ms ease;
}

.answer-show-more-sources:hover {
  background: #f8fafc;
  border-color: rgba(15, 23, 42, 0.22);
  transform: translateY(-1px);
}

.answer-show-more-sources:active {
  transform: translateY(0);
}

/* =========================
   Tables
   ========================= */

.answer-table-region {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: 14px 0 16px;
  overflow: visible;
  border-radius: 14px;
}

.answer-table-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scroll-behavior: smooth;
  scrollbar-gutter: stable;
  border-radius: 14px;
  background: #ffffff;
  position: relative;
}

.answer-table-scroll-fits {
  overflow-x: hidden;
}

.answer-table-scroll-has-overflow {
  overflow-x: auto;
  padding-bottom: 4px;
}

.answer-table-scroll-instruction {
  display: block;
  width: 100%;
  padding: 7px 10px 1px;
  color: #64748b;
  font-size: 11.5px;
  line-height: 1.35;
  font-weight: 700;
  text-align: right;
  white-space: normal;
}

/*
  Keep horizontal scrolling available, but do not let long text force
  one giant single-line table. The table has a sensible minimum width
  and cells have controlled min/max widths.
*/
.answer-table-wrap {
  width: max-content;
  min-width: 100%;
  max-width: none;
  margin: 0;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: #ffffff;
  overflow: hidden;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.answer-table {
  width: max-content;
  min-width: 100%;
  max-width: none;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  line-height: 1.46;
  white-space: normal;
  background: #ffffff;
  table-layout: auto;
}

/*
  Header cells:
  - wrap by words
  - never break letters vertically
  - keep professional spacing
*/
.answer-table th {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  color: #0f172a;
  font-weight: 850;
  text-align: left;
  padding: 11px 13px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.13);
  border-right: 1px solid rgba(15, 23, 42, 0.07);
  vertical-align: top;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
}

/*
  Body cells:
  - wrap long sentences naturally
  - avoid one huge line across the page
  - preserve scroll for genuinely wide tables
*/
.answer-table td {
  padding: 11px 13px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.075);
  border-right: 1px solid rgba(15, 23, 42, 0.055);
  color: #111827;
  vertical-align: top;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: auto;
}

.answer-table th:last-child,
.answer-table td:last-child {
  border-right: none;
}

.answer-table tr:last-child td {
  border-bottom: none;
}

.answer-table tbody tr:nth-child(even) td {
  background: rgba(248, 250, 252, 0.55);
}

.answer-table tbody tr:hover td {
  background: rgba(239, 246, 255, 0.7);
}

.answer-table-cell-content {
  display: block;
  max-width: 100%;
  white-space: normal;
}

/*
  Smart column sizing from AnswerTable.jsx.
  These classes prevent long explanation columns from stretching forever.
*/
.answer-table-cell-short {
  min-width: 92px;
  max-width: 150px;
}

.answer-table-cell-medium {
  min-width: 140px;
  max-width: 260px;
}

.answer-table-cell-long {
  min-width: 260px;
  max-width: 460px;
}

.answer-table-cell-numeric {
  min-width: 96px;
  max-width: 150px;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.answer-table-cell-date {
  min-width: 118px;
  max-width: 170px;
  white-space: normal;
}

.answer-table-cell-status {
  min-width: 110px;
  max-width: 170px;
}

.answer-table-cell-align-number {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.answer-table-cell-status-value {
  font-weight: 750;
}

/*
  If the table contains long legal/financial explanations, allow wider
  readable cells but still wrap within the cell instead of stretching forever.
*/
.answer-table-has-long-text .answer-table-cell-long {
  min-width: 300px;
  max-width: 520px;
}

.answer-table-has-long-text td.answer-table-cell-long .answer-table-cell-content {
  line-height: 1.5;
}

/*
  Many-column tables should stay compact enough to remain usable.
*/
.answer-table-many-cols {
  font-size: 13.5px;
}

.answer-table-many-cols th,
.answer-table-many-cols td {
  padding: 9px 11px;
}

.answer-table-many-cols .answer-table-cell-medium {
  min-width: 125px;
  max-width: 220px;
}

.answer-table-many-cols .answer-table-cell-long {
  min-width: 220px;
  max-width: 380px;
}

/*
  Compact 2-3 column tables often contain longer explanation fields.
*/
.answer-table-compact-cols .answer-table-cell-medium {
  min-width: 180px;
  max-width: 340px;
}

.answer-table-compact-cols .answer-table-cell-long {
  min-width: 320px;
  max-width: 560px;
}

.answer-table-scroll:focus-visible {
  outline: 2px solid rgba(37, 99, 235, 0.32);
  outline-offset: 2px;
}

/*
  Fallback when a block looked table-like but was not a valid markdown table.
  Keep this light/professional, not black terminal-style.
*/
.answer-plain-code-block {
  margin: 12px 0;
  padding: 11px 12px;
  border-radius: 12px;
  background: #f8fafc;
  color: #111827;
  border: 1px solid rgba(15, 23, 42, 0.12);
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: normal;
  overflow-wrap: break-word;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.45;
}

/* =========================
   Math / Formula
   ========================= */

.answer-math-card {
  margin: 12px 0 14px;
  padding: 14px 16px 15px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
  max-width: 100%;
  overflow: hidden;
}

.answer-math-label {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 850;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.045em;
}

.answer-math-summary {
  margin: -2px 0 8px;
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
}

.answer-math-content-katex {
  font-family: inherit;
  white-space: normal;
}

.answer-math-content .katex-display {
  margin: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 2px 0;
}

.answer-math-content .katex {
  font-size: 1.08em;
}

.answer-math-line-result {
  background: rgba(22, 163, 74, 0.06);
  border-radius: 8px;
  padding-left: 7px;
}

.answer-math-line-note {
  color: #475569;
}

.answer-math-line-reaction {
  background: rgba(37, 99, 235, 0.045);
  border-radius: 8px;
  padding-left: 7px;
}

.answer-math-content {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding: 10px 11px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.08);
  color: #0f172a;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 15px;
  line-height: 1.7;
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
  box-sizing: border-box;
}

.answer-math-line {
  display: block;
  min-width: max-content;
  margin: 0;
  padding: 1px 0;
}

.answer-math-line + .answer-math-line {
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px dashed rgba(15, 23, 42, 0.1);
}

.answer-math-token {
  display: inline;
}

.answer-math-token-text {
  color: #0f172a;
}

.answer-math-token-identifier {
  color: #1e3a8a;
  font-weight: 700;
}

.answer-math-token-number {
  color: #047857;
  font-weight: 750;
}

.answer-math-token-operator {
  color: #b45309;
  font-weight: 850;
  padding: 0 4px;
}

.answer-math-token-symbol {
  color: #7c3aed;
  font-weight: 800;
}

.answer-math-token-punctuation {
  color: #475569;
  font-weight: 650;
}

/* Legacy safety if an older AnswerMath still outputs breakdown classes */
.answer-math-breakdown {
  display: none;
}

/* =========================
   Chart / Graph / Geometry / Diagram
   ========================= */

.answer-chart-card {
  width: 100%;
  max-width: 100%;
  margin: 14px 0 16px;
  padding: 16px;
  border-radius: 18px;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.12);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.055);
  overflow: hidden;
}

.answer-chart-label {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 850;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.045em;
}

.answer-chart-subtitle {
  margin-top: -2px;
  margin-bottom: 10px;
  font-size: 13px;
  color: #475569;
  line-height: 1.4;
}

/*
  IMPORTANT:
  The old version capped this at 520px. That is too narrow for:
  scatter, bubble, heatmap, gantt, waterfall, candlestick, histogram,
  radar, boxplot, timelines, and scientific diagrams.
*/
.answer-chart-preview {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  min-height: 260px;
  margin: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scroll-behavior: smooth;
  scrollbar-gutter: stable;
  border-radius: 14px;
  background: #f8fafc;
  position: relative;
}

.answer-chart-canvas {
  display: inline-block;
  width: max-content;
  min-width: 100%;
  max-width: none;
  min-height: inherit;
  background: #f8fafc;
  border-radius: inherit;
  vertical-align: top;
  overflow: visible;
}

.answer-chart-canvas > * {
  width: max-content;
  min-width: 100%;
  max-width: none;
}

.answer-chart-preview[data-has-horizontal-overflow="true"] {
  padding-bottom: 4px;
}

/*
  Recharts responsive safety.
  Prevent chart canvases/SVGs from inheriting bad parent sizing.
*/
.answer-chart-card .recharts-responsive-container {
  width: auto !important;
  max-width: none !important;
  min-width: 100% !important;
}

.answer-chart-card .recharts-wrapper {
  width: auto !important;
  max-width: none !important;
  min-width: 100% !important;
}

.answer-chart-card .recharts-surface {
  max-width: none !important;
  overflow: visible;
}

.answer-chart-card .recharts-legend-wrapper {
  width: 100% !important;
  max-width: 100% !important;
  overflow: visible !important;
  white-space: normal !important;
  padding-left: 8px !important;
  padding-right: 8px !important;
}

.answer-chart-card .recharts-default-legend {
  display: flex !important;
  flex-wrap: wrap !important;
  justify-content: center;
  align-items: center;
  gap: 7px 12px;
  max-width: 100% !important;
  margin: 0 auto !important;
  padding: 0 8px !important;
  white-space: normal !important;
}

.answer-chart-card .recharts-legend-item {
  display: inline-flex !important;
  align-items: center;
  min-width: 0;
  max-width: 240px;
  margin-right: 0 !important;
  white-space: normal !important;
}

.answer-chart-card .recharts-legend-item-text {
  display: inline-block;
  min-width: 0;
  max-width: 210px;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: normal;
  line-height: 1.3;
}

.answer-chart-card .recharts-tooltip-wrapper {
  max-width: min(320px, calc(100vw - 48px));
  z-index: 5;
}

.answer-chart-card .recharts-cartesian-axis-tick text,
.answer-chart-card .recharts-polar-angle-axis-tick-value,
.answer-chart-card .recharts-legend-item-text {
  font-family: inherit;
}

/*
  SVG defaults are intentionally light.
  Inline styles inside AnswerChart.jsx control chart-specific colors.
*/
.answer-chart-svg {
  width: auto;
  min-width: 100%;
  max-width: none;
  height: 100%;
  min-height: 220px;
  display: block;
  overflow: visible;
  background: #f8fafc;
}

/*
  Do NOT force one blue stroke/fill on chart shapes globally.
  The chart component uses inline styles for business chart colors.
*/
.answer-chart-svg line,
.answer-chart-svg path,
.answer-chart-svg circle,
.answer-chart-svg ellipse,
.answer-chart-svg rect,
.answer-chart-svg polygon {
  vector-effect: non-scaling-stroke;
}

.answer-chart-description {
  display: none;
}

/*
  Scrollbar polish for wide charts/tables.
*/
.answer-chart-preview::-webkit-scrollbar,
.answer-table-scroll::-webkit-scrollbar,
.answer-math-content::-webkit-scrollbar {
  height: 9px;
}

.answer-chart-preview::-webkit-scrollbar-track,
.answer-table-scroll::-webkit-scrollbar-track,
.answer-math-content::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.04);
  border-radius: 999px;
}

.answer-chart-preview::-webkit-scrollbar-thumb,
.answer-table-scroll::-webkit-scrollbar-thumb,
.answer-math-content::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.18);
  border-radius: 999px;
}

.answer-chart-preview::-webkit-scrollbar-thumb:hover,
.answer-table-scroll::-webkit-scrollbar-thumb:hover,
.answer-math-content::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 23, 42, 0.28);
}

.answer-chart-preview,
.answer-table-scroll,
.answer-math-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.28) rgba(15, 23, 42, 0.04);
}

/* =========================
   Streaming
   ========================= */

.answer-renderer-streaming .answer-chart-card,
.answer-renderer-streaming .answer-math-card {
  animation: answer-card-soft-in 160ms ease-out;
}

@keyframes answer-card-soft-in {
  from {
    opacity: 0.7;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* =========================
   Mobile
   ========================= */

@media (max-width: 1023px) {
  .answer-renderer {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    font-size: 15.5px;
    line-height: 1.48;
    overflow-x: hidden;
  }

  .answer-renderer > *,
  .answer-text,
  .answer-table-region,
  .answer-table-scroll,
  .answer-chart-card,
  .answer-chart-preview,
  .answer-math-card {
    min-width: 0;
    max-width: 100%;
  }

  .answer-section-label {
    margin-top: 12px;
  }

  .answer-source-link {
    width: 100%;
    box-sizing: border-box;
    padding: 7px 9px;
  }

  /*
    Do not force every table to a fixed mobile width.
    Narrow tables fit naturally; genuinely wide tables create their own
    independent horizontal scrollbar.
  */
  .answer-table {
    min-width: 100%;
    font-size: 13.5px;
  }

  .answer-table th,
  .answer-table td {
    padding: 9px 10px;
  }

  .answer-table-cell-short {
    min-width: 88px;
    max-width: 130px;
  }

  .answer-table-cell-medium {
    min-width: 130px;
    max-width: 220px;
  }

  .answer-table-cell-long {
    min-width: 240px;
    max-width: 360px;
  }

  .answer-table-compact-cols .answer-table-cell-long {
    min-width: 260px;
    max-width: 420px;
  }

  .answer-table-scroll-has-overflow {
    padding-bottom: 6px;
  }

  .answer-table-scroll-instruction {
    padding: 7px 8px 1px;
    font-size: 11px;
  }

  .answer-math-card {
    padding: 12px 12px 14px;
    border-radius: 14px;
  }

  .answer-math-content {
    font-size: 14px;
    padding: 9px 10px;
  }

  .answer-chart-card {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 12px;
    border-radius: 14px;
    margin: 12px 0 14px;
    overflow: hidden;
  }

  .answer-chart-label {
    font-size: 11.5px;
    margin-bottom: 7px;
  }

  .answer-chart-subtitle {
    font-size: 12.5px;
    margin-bottom: 8px;
  }

  .answer-chart-preview {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    min-height: 240px;
    border-radius: 12px;
  }

  .answer-chart-canvas {
    width: max-content;
    min-width: 100%;
    max-width: none;
    background: #f8fafc;
  }

  .answer-chart-card .recharts-wrapper,
  .answer-chart-card .recharts-responsive-container {
    width: auto !important;
    max-width: none !important;
    min-width: 100% !important;
  }

  .answer-chart-card .recharts-default-legend {
    justify-content: flex-start !important;
    font-size: 12px;
    gap: 7px 10px;
  }

  .answer-chart-card .recharts-legend-item {
    max-width: 210px;
  }

  .answer-chart-card .recharts-legend-item-text {
    max-width: 180px;
    font-size: 12px !important;
  }

  .answer-chart-card .recharts-cartesian-axis-tick text {
    font-size: 11px;
  }

  .answer-chart-card .recharts-tooltip-wrapper {
    max-width: min(260px, calc(100vw - 40px)) !important;
  }

  .answer-chart-svg {
    width: auto;
    min-width: 100%;
    max-width: none;
    min-height: 220px;
  }

  .answer-chart-preview::-webkit-scrollbar,
  .answer-table-scroll::-webkit-scrollbar,
  .answer-math-content::-webkit-scrollbar {
    height: 10px;
  }
}

/* =========================
   Print
   ========================= */

@media print {
  .answer-renderer {
    color: #111827;
    font-size: 11px;
    line-height: 1.35;
  }

  .answer-show-more-sources {
    display: none !important;
  }

  .answer-sources-list {
    gap: 4px;
  }

  .answer-source-link {
    border: none;
    background: transparent;
    padding: 2px 0;
    color: #111827;
  }

  .answer-table-region,
  .answer-table-scroll {
    overflow: visible;
  }

  .answer-table-scroll-instruction {
    display: none !important;
  }

  .answer-table-wrap {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: visible;
  }

  .answer-table {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    font-size: 10.5px;
    table-layout: fixed;
  }

  .answer-table th,
  .answer-table td {
    padding: 6px 7px;
    white-space: normal;
    overflow-wrap: break-word;
    word-break: normal;
    hyphens: auto;
  }

  .answer-table-cell-short,
  .answer-table-cell-medium,
  .answer-table-cell-long,
  .answer-table-cell-numeric,
  .answer-table-cell-date,
  .answer-table-cell-status {
    min-width: 0;
    max-width: none;
  }

  .answer-plain-code-block {
    white-space: pre-wrap;
    background: #f8fafc;
    color: #111827;
    border: 1px solid rgba(15, 23, 42, 0.12);
  }

  .answer-math-card,
  .answer-chart-card {
    box-shadow: none;
    break-inside: avoid;
    page-break-inside: avoid;
    border: 1px solid rgba(15, 23, 42, 0.16);
  }

  .answer-math-content {
    white-space: normal;
    overflow: visible;
    font-size: 10.5px;
    line-height: 1.45;
  }

  .answer-math-line {
    min-width: 0;
    white-space: normal;
  }

  .answer-chart-card {
    padding: 10px;
    margin: 10px 0 12px;
  }

  .answer-chart-preview {
    width: 100%;
    max-width: 100%;
    min-height: 220px;
    overflow: visible;
  }

  .answer-chart-canvas {
    display: block;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    background: #ffffff;
  }

  .answer-chart-card .recharts-wrapper,
  .answer-chart-card .recharts-responsive-container {
    width: 100% !important;
    max-width: 100% !important;
  }

  .answer-chart-svg {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: auto;
    min-height: 180px;
  }
}
`;

let answerRendererStyleUsers = 0;

const useSafeLayoutEffect =
  typeof window !== "undefined" && typeof document !== "undefined"
    ? useLayoutEffect
    : useEffect;

function useAnswerRendererStyles() {
  useSafeLayoutEffect(() => {
    if (typeof document === "undefined") return undefined;

    let styleEl = document.getElementById(ANSWER_RENDERER_STYLE_ID);
    let ownedByRenderer = false;

    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = ANSWER_RENDERER_STYLE_ID;
      styleEl.setAttribute("data-asklawly-owned", "true");
      styleEl.textContent = ANSWER_RENDERER_CSS;
      document.head.appendChild(styleEl);
      ownedByRenderer = true;
    } else if (styleEl.getAttribute("data-asklawly-owned") === "true") {
      ownedByRenderer = true;

      // Hot-reload/code-update safety: keep the single mounted stylesheet current.
      if (styleEl.textContent !== ANSWER_RENDERER_CSS) {
        styleEl.textContent = ANSWER_RENDERER_CSS;
      }
    }

    answerRendererStyleUsers += 1;

    return () => {
      answerRendererStyleUsers = Math.max(0, answerRendererStyleUsers - 1);

      if (answerRendererStyleUsers !== 0 || !ownedByRenderer) return;

      const current = document.getElementById(ANSWER_RENDERER_STYLE_ID);
      if (current?.getAttribute("data-asklawly-owned") === "true") {
        current.remove();
      }
    };
  }, []);
}

/**
 * AnswerRenderer
 *
 * Main presentation controller for assistant answers.
 *
 * Responsibilities:
 * - Render normal answer text cleanly.
 * - Remove raw markdown citation clutter from the visible body.
 * - Convert inline markdown links into citation markers like [1], [2].
 * - Extract and normalize the Sources section.
 * - Deduplicate source URLs.
 * - Preserve source relationships between inline citations and the bottom Sources list.
 * - Detect markdown tables and send them to AnswerTable.
 * - Leave room for math, chemistry, graphs, geometry, and education rendering later.
 *
 * Streaming behavior:
 * - Final saved Lexa messages: full rendering, citations, sources, tables, math, charts.
 * - Live streaming text: safer rendering to avoid flicker while markdown/source blocks are incomplete.
 */

/**
 * More forgiving source heading detection.
 *
 * We still prefer the plain heading "Sources", but Lexa/model output may produce:
 * - Sources (used to verify)
 * - Sources used to verify
 * - Sources (clean list)
 * - Clean numbered sources
 * - References
 * - Authorities
 * - Citations
 *
 * This renderer treats those as the same bottom source section.
 */
const SOURCE_SECTION_RE =
  /^\s*(sources?|references?|authorities|citations?|source\s+list|clean\s+numbered\s+sources|source\s+references)(?:\s*\([^)]*\)|\s+(?:used|reviewed|consulted|verified|clean|numbered|for|to)\b.*)?\s*:?\s*$/i;

/**
 * Headings that can appear AFTER a Sources section.
 * Used to avoid accidentally swallowing a follow-up/clarifying question into the source list.
 */
const SECTION_HEADING_RE =
  /^\s*(Findings|Follow[-\s]?up|Clarifying question|One clarifying question|One quick question|One quick clarifier|Question|Questions|Notes|Important|Important note|Summary|Bottom line|Practical bottom line|Takeaway|Next|Next steps?)\s*:?\s*$/i;

const MARKDOWN_LINK_RE = /\[([^\]]+)\]\s*\((https?:\/\/[^)\s]+)\)/gi;

const WRAPPED_MARKDOWN_CITATION_RE = /\(\s*\[([^\]]+)\]\s*\((https?:\/\/[^)\s]+)\)\s*\)/gi;

const BARE_URL_RE = /(https?:\/\/[^\s<>)]+)(?=[\s.)\],;:!?]|$)/gi;

const TABLE_SEPARATOR_RE = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;

// Raw LaTeX display/inline math delimiters that Lexa may emit directly.
// These must be detected before AnswerText receives them, otherwise users see
// literal \\[ ... \\] / $$ ... $$ lines instead of rendered KaTeX.
const DISPLAY_MATH_OPEN_RE = /^\s*(?:\\\[|\$\$)\s*$/;
const DISPLAY_MATH_CLOSE_RE = /^\s*(?:\\\]|\$\$)\s*$/;
const SINGLE_LINE_DISPLAY_MATH_RE = /^\s*(?:\\\[([\s\S]+?)\\\]|\$\$([\s\S]+?)\$\$)\s*$/;
const SINGLE_LINE_PAREN_MATH_RE = /^\s*\\\(([\s\S]+?)\\\)\s*$/;

function stripMathFenceLine(line) {
  return String(line || "")
    .replace(/^\s*\\\[/, "")
    .replace(/\\\]\s*$/, "")
    .replace(/^\s*\$\$/, "")
    .replace(/\$\$\s*$/, "")
    .replace(/^\s*\\\(/, "")
    .replace(/\\\)\s*$/, "")
    .trim();
}

function getSingleLineMathBlock(line) {
  const value = String(line || "");
  const display = value.match(SINGLE_LINE_DISPLAY_MATH_RE);
  if (display) {
    const content = String(display[1] || display[2] || "").trim();
    return content ? { type: "math", content } : null;
  }

  const paren = value.match(SINGLE_LINE_PAREN_MATH_RE);
  if (paren) {
    const content = String(paren[1] || "").trim();
    return content ? { type: "math", content } : null;
  }

  return null;
}

function collectDisplayMathBlock(lines, startIndex) {
  const collected = [];
  let i = startIndex + 1;

  while (i < lines.length) {
    const line = lines[i] || "";
    if (DISPLAY_MATH_CLOSE_RE.test(line)) {
      return { content: collected.join("\n").trim(), nextIndex: i + 1, closed: true };
    }
    collected.push(line);
    i += 1;
  }

  return { content: collected.join("\n").trim(), nextIndex: i, closed: false };
}

const ORDINARY_FILE_EXTENSION_RE =
  /\.(?:pdf|docx?|xlsx?|pptx?|csv|txt|rtf|odt|ods|odp|pages|numbers|key|json|xml|html?|md|zip|png|jpe?g|gif|webp|heic|heif|svg)\b/i;

const WINDOWS_OR_UNIX_PATH_RE =
  /(?:[A-Za-z]:\\(?:[^\\\r\n]+\\)*[^\\\r\n]*|(?:^|\s)\/(?:[^/\s]+\/)+[^/\s]*)/;

const EMAIL_LIKE_RE =
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;

const ISO_OR_SLASH_DATE_RE =
  /\b(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})\b/;

const STRONG_LATEX_MATH_COMMAND_RE =
  /\\(?:frac|dfrac|tfrac|sqrt|sum|prod|int|iint|iiint|lim|log|ln|sin|cos|tan|arcsin|arccos|arctan|left|right|begin|end|matrix|pmatrix|bmatrix|cases|alpha|beta|gamma|delta|epsilon|theta|lambda|mu|sigma|phi|psi|omega|pi|infty|partial|nabla|cdot|times|div|pm|mp|leq?|geq?|neq|approx|equiv|propto)\b/;

const MATH_RELATION_RE =
  /(?:=|≈|≠|≤|≥|<=>|=>|<=|>=|\\leq?|\\geq?|\\neq|\\approx|\\equiv|\\propto)/;

const MATH_MINUS_RE =
  /(?:\b[A-Za-z]\b|\b[A-Z]{2,8}\b|\d+(?:\.\d+)?|[)\]}])\s*-\s*(?:\b[A-Za-z]\b|\b[A-Z]{2,8}\b|\d+(?:\.\d+)?|[(\[{])/;

const MATH_ARITHMETIC_RE =
  /(?:\+|[×÷·]|\\times\b|\\cdot\b|\\div\b|\/|\*)/;

const PAGE_REFERENCE_RE =
  /\b(?:page|pages|p\.?)\s*#?\s*\d+(?:\s*(?:-|–|—|to|\/)\s*\d+)?\b/gi;

const MATH_POWER_RE =
  /(?:\^\{[^}]+\}|\^[A-Za-z0-9()+-]+)/;

const MATH_BRACED_SUBSCRIPT_RE =
  /_\{[^}]+\}/;

const MATH_SHORT_VARIABLE_SUBSCRIPT_RE =
  /\b[A-Za-z]{1,4}_[A-Za-z0-9]{1,5}\b/;

const MATH_FUNCTION_RE =
  /\b(?:f|g|h|P|PV|FV|NPV|IRR|ROI|APR|r|x|y|z|sin|cos|tan|log|ln|exp|sqrt|max|min)\s*\([^)]*\)/i;

const MATH_SYMBOL_RE =
  /[∑∫√∞πθλμσΔ∇±∓]/;

const CURRENCY_AMOUNT_RE =
  /(?:[$€£¥]\s*\d|\b(?:USD|EUR|GBP|JPY)\s+\d)/i;

function countNaturalLanguageWords(value) {
  return (String(value || "").match(/\b[A-Za-z]{3,}\b/g) || []).length;
}

function looksLikeOrdinaryNonMathLine(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return true;

  if (/^\s*[-•*]\s+/.test(trimmed)) return true;
  if (/^\s*\d+[.)]\s+/.test(trimmed)) return true;

  // Obvious document/file/path/address syntax should never become guessed math.
  if (/https?:\/\//i.test(trimmed)) return true;
  if (EMAIL_LIKE_RE.test(trimmed)) return true;
  if (ORDINARY_FILE_EXTENSION_RE.test(trimmed)) return true;
  if (WINDOWS_OR_UNIX_PATH_RE.test(trimmed)) return true;

  // Markdown/citation strings are ordinary text unless explicitly marked math.
  if (/\[[^\]]+\]\([^)]*\)/.test(trimmed)) return true;

  // Document-language is a strong prose signal. Only let it pass when the same
  // line also contains a genuinely mathematical relation/operator/symbol.
  if (/\b(?:document|attachment|file|page|pages|section|terms?|agreement|contract|loan|brief|summary|risk|risks|obligation|obligations|party|parties)\b/i.test(trimmed)) {
    const hasStrongMath =
      STRONG_LATEX_MATH_COMMAND_RE.test(trimmed) ||
      MATH_RELATION_RE.test(trimmed) ||
      MATH_ARITHMETIC_RE.test(trimmed) ||
      MATH_SYMBOL_RE.test(trimmed);

    if (!hasStrongMath) return true;
  }

  // Dates are prose unless they are clearly participating in an equation.
  if (ISO_OR_SLASH_DATE_RE.test(trimmed)) {
    const hasEquationAroundDate =
      MATH_RELATION_RE.test(trimmed) &&
      MATH_ARITHMETIC_RE.test(trimmed);
    if (!hasEquationAroundDate) return true;
  }

  // A dollar sign is NOT a math delimiter. Money sentences stay prose, while
  // real currency arithmetic such as "$100 + $20 = $120" can still be math.
  if (CURRENCY_AMOUNT_RE.test(trimmed)) {
    const hasCurrencyMath =
      MATH_RELATION_RE.test(trimmed) ||
      MATH_ARITHMETIC_RE.test(trimmed) ||
      MATH_POWER_RE.test(trimmed);

    if (!hasCurrencyMath) return true;
  }

  return false;
}

function looksLikeStandaloneMathLine(line) {
  const trimmed = String(line || "").trim();
  if (!trimmed) return false;

  /*
    Explicit math delimiters and [math]/[formula]/[finance]/[calculation]
    markers are handled elsewhere. This heuristic only guesses whether an
    UNMARKED isolated line is sufficiently equation-like for AnswerMath.
  */
  if (looksLikeOrdinaryNonMathLine(trimmed)) return false;

  const hasStrongLatex = STRONG_LATEX_MATH_COMMAND_RE.test(trimmed);
  if (hasStrongLatex) return true;

  /*
    Remove page-reference metadata before measuring mathematical punctuation.
    "(Page 1)" and "Page 1 / 10" are document provenance, not math structure.
  */
  const mathSignalText =
    trimmed.replace(
      PAGE_REFERENCE_RE,
      " "
    );

  const hasRelation =
    MATH_RELATION_RE.test(
      mathSignalText
    );

  const hasArithmetic =
    MATH_ARITHMETIC_RE.test(
      mathSignalText
    ) ||
    MATH_MINUS_RE.test(
      mathSignalText
    );

  const hasPower =
    MATH_POWER_RE.test(
      mathSignalText
    );

  const hasBracedSubscript =
    MATH_BRACED_SUBSCRIPT_RE.test(
      mathSignalText
    );

  const hasShortVariableSubscript =
    MATH_SHORT_VARIABLE_SUBSCRIPT_RE.test(
      mathSignalText
    );

  const hasFunction =
    MATH_FUNCTION_RE.test(
      mathSignalText
    );

  const hasMathSymbol =
    MATH_SYMBOL_RE.test(
      mathSignalText
    );

  const hasDigit =
    /\d/.test(
      mathSignalText
    );

  const hasParen =
    /[()[\]{}]/.test(
      mathSignalText
    );

  const naturalWordCount =
    countNaturalLanguageWords(
      trimmed
    );

  /*
    A full English sentence must not become math merely because it contains a
    dash, slash, number, or parenthetical page reference. Long prose needs a
    genuinely mathematical signal such as a relation, power, subscript,
    function, mathematical symbol, or explicit LaTeX.
  */
  if (
    naturalWordCount >= 6 &&
    !hasRelation &&
    !hasPower &&
    !hasBracedSubscript &&
    !hasShortVariableSubscript &&
    !hasFunction &&
    !hasMathSymbol
  ) {
    return false;
  }

  // Canonical compact equations: x = 2, E = mc^2, PV = FV/(1+r)^n.
  const simpleEquation = trimmed.match(
    /^\s*([A-Za-z][A-Za-z0-9_]{0,7}(?:\([^)]*\))?)\s*(=|≈|≠|≤|≥|<=|>=)\s*(.+?)\s*$/
  );

  if (simpleEquation) {
    const lhs = String(simpleEquation[1] || "");
    const rhs = String(simpleEquation[3] || "");

    const rhsHasMathSignal =
      /\d/.test(rhs) ||
      MATH_ARITHMETIC_RE.test(rhs) ||
      MATH_POWER_RE.test(rhs) ||
      MATH_BRACED_SUBSCRIPT_RE.test(rhs) ||
      MATH_SHORT_VARIABLE_SUBSCRIPT_RE.test(rhs) ||
      MATH_FUNCTION_RE.test(rhs) ||
      MATH_SYMBOL_RE.test(rhs) ||
      STRONG_LATEX_MATH_COMMAND_RE.test(rhs);

    if (lhs.length <= 4 || rhsHasMathSignal) {
      return true;
    }
  }

  // A relation needs another mathematical signal. This blocks prose such as
  // "Status = ready" from becoming a formula card.
  if (
    hasRelation &&
    (
      hasArithmetic ||
      hasPower ||
      hasBracedSubscript ||
      hasShortVariableSubscript ||
      hasFunction ||
      hasMathSymbol ||
      hasDigit
    )
  ) {
    return true;
  }

  // Unmarked expressions need multiple independent math signals and little prose.
  const structuralSignals = [
    hasArithmetic,
    hasPower,
    hasBracedSubscript,
    hasShortVariableSubscript,
    hasFunction,
    hasMathSymbol,
    hasParen && hasDigit,
  ].filter(Boolean).length;

  if (structuralSignals >= 2 && naturalWordCount <= 4) {
    return true;
  }

  // Common function notation such as sin(x), log(x), or f(x).
  if (
    hasFunction &&
    naturalWordCount <= 1
  ) {
    return true;
  }

  // Numeric arithmetic such as "2 + 2" or "(1+r)^12 - 1".
  if (
    hasDigit &&
    hasArithmetic &&
    naturalWordCount <= 2 &&
    (
      hasPower ||
      hasParen ||
      naturalWordCount === 0
    )
  ) {
    return true;
  }

  return false;
}



const HARD_MATH_MARKER_TYPES = new Set([
  "math",
  "formula",
  "equation",
]);

const CONDITIONAL_MATH_MARKER_TYPES = new Set([
  "chemistry",
  "physics",
  "science",
  "statistics",
  "probability",
  "finance",
  "accounting",
  "calculation",
]);

function looksLikeMathPayload(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  // Explicit TeX inside a marker payload is conclusive.
  if (
    SINGLE_LINE_DISPLAY_MATH_RE.test(text) ||
    SINGLE_LINE_PAREN_MATH_RE.test(text) ||
    /\\\[[\s\S]*?\\\]/.test(text) ||
    /\$\$[\s\S]*?\$\$/.test(text) ||
    /\\\([\s\S]*?\\\)/.test(text)
  ) {
    return true;
  }

  if (STRONG_LATEX_MATH_COMMAND_RE.test(text)) {
    return true;
  }

  // A domain marker may wrap more than one line. One genuinely mathematical
  // line is enough, but prose-only lines never become math merely because the
  // marker said "finance", "accounting", "statistics", etc.
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (
    lines.some((line) =>
      looksLikeStandaloneMathLine(line)
    )
  ) {
    return true;
  }

  // Catch readable business/scientific equations whose left side is a phrase:
  //   Gross profit = Revenue - COGS
  //   Net income = Revenue - Expenses
  //   Mean = Sum / Count
  const phraseEquation = text.match(
    /(?:^|\n)\s*([A-Za-z][A-Za-z0-9 ()/%.-]{0,48})\s*(=|≈|≠|≤|≥|<=|>=)\s*([^\n]+)\s*(?:$|\n)/
  );

  if (phraseEquation) {
    const rhs = String(
      phraseEquation[3] || ""
    ).trim();

    const rhsHasOperator =
      /(?:\+|[×÷·−]|\s-\s|\*|\/|\\times\b|\\cdot\b|\\div\b)/.test(
        rhs
      );

    const rhsHasMathStructure =
      rhsHasOperator ||
      MATH_POWER_RE.test(rhs) ||
      MATH_BRACED_SUBSCRIPT_RE.test(rhs) ||
      MATH_SHORT_VARIABLE_SUBSCRIPT_RE.test(rhs) ||
      MATH_FUNCTION_RE.test(rhs) ||
      MATH_SYMBOL_RE.test(rhs) ||
      STRONG_LATEX_MATH_COMMAND_RE.test(rhs) ||
      /\d/.test(rhs);

    if (rhsHasMathStructure) {
      return true;
    }
  }

  // Currency by itself remains prose, but real currency arithmetic is math.
  if (
    CURRENCY_AMOUNT_RE.test(text) &&
    MATH_RELATION_RE.test(text) &&
    /(?:\+|[×÷·−]|\s-\s|\*|\/)/.test(text)
  ) {
    return true;
  }

  return false;
}

function resolveSpecialBlockType(rawType, content) {
  const type = String(rawType || "")
    .trim()
    .toLowerCase();

  if (
    HARD_MATH_MARKER_TYPES.has(type) ||
    CONDITIONAL_MATH_MARKER_TYPES.has(type)
  ) {
    // Marker names alone are not enough. Genuine equations, formulas, TeX,
    // arithmetic, chemistry notation, and scientific expressions still pass.
    return looksLikeMathPayload(content)
      ? "math"
      : "text";
  }

  if (type === "table") {
    return looksLikeTablePayload(content)
      ? "table"
      : "text";
  }

  if (
    GENERIC_VISUAL_MARKER_TYPES.has(type) ||
    DATA_VISUAL_MARKER_TYPES.has(type) ||
    GEOMETRY_VISUAL_TYPES.has(type)
  ) {
    return looksLikeVisualPayload(
      type,
      content
    )
      ? "chart"
      : "text";
  }

  return null;
}

/**
 * Special block markers.
 *
 * Important architecture:
 * - Math/topic markers are validated before routing to AnswerMath.
 * - Chart/diagram markers are validated before routing to AnswerChart.
 * - [table] is accepted only when the payload is structurally table-like.
 * - Invalid/ambiguous special markers fall back to ordinary AnswerText.
 *
 * This keeps the renderer permissive toward genuine structured output while
 * refusing to manufacture a specialized presentation from weak wording alone.
 */
const GENERIC_VISUAL_MARKER_TYPES = new Set([
  "chart",
  "graph",
  "geometry",
  "plot",
  "diagram",
  "visual",
  "curve",
  "figure",
  "drawing",
  "svg",
  "shape",
]);

const DATA_VISUAL_MARKER_TYPES = new Set([
  "bar",
  "column",
  "grouped_bar",
  "grouped_column",
  "stacked_bar",
  "stacked_column",
  "line",
  "area",
  "stacked_area",
  "composed",
  "combo",
  "pie",
  "donut",
  "scatter",
  "bubble",
  "histogram",
  "radar",
  "boxplot",
  "box_plot",
  "waterfall",
  "heatmap",
  "funnel",
  "treemap",
  "treemap_basic",
  "gauge",
  "progress",
  "timeline",
  "gantt",
  "gantt_basic",
  "candlestick",
  "candlestick_basic",
  "pareto",
  "slope",
  "lollipop",
  "dotplot",
  "bell_curve",
  "confidence_interval",
  "error_bar",
  "confidence_band",
  "probability_band",
  "kde_density",
  "roc_curve",
  "precision_recall",
  "violin_plot",
]);

const GEOMETRY_VISUAL_TYPES = new Set([
  "circle",
  "ellipse",
  "oval",
  "square",
  "rectangle",
  "triangle",
  "right_triangle",
  "angle",
  "parabola",
  "coordinate_graph",
  "coordinate_plane",
  "sine_wave",
  "cosine_wave",
  "number_line",
  "venn",
  "flowchart",
  "flowchart_basic",
]);

const SPECIAL_MARKER_TYPES = [
  ...HARD_MATH_MARKER_TYPES,
  ...CONDITIONAL_MATH_MARKER_TYPES,
  ...GENERIC_VISUAL_MARKER_TYPES,
  ...DATA_VISUAL_MARKER_TYPES,
  ...GEOMETRY_VISUAL_TYPES,
  "table",
];

const SPECIAL_BLOCK_RE = new RegExp(
  `^\\s*\\[(${SPECIAL_MARKER_TYPES
    .map((type) =>
      String(type).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    )
    .join("|")})\\]\\s*(.*)$`,
  "i"
);

function stripTracking(url) {
  try {
    const u = new URL(url);
    u.searchParams.delete("utm_source");
    u.searchParams.delete("utm_medium");
    u.searchParams.delete("utm_campaign");
    u.searchParams.delete("utm_term");
    u.searchParams.delete("utm_content");
    return u.toString();
  } catch {
    return String(url || "").trim();
  }
}

function cleanUrl(url) {
  return stripTracking(String(url || "").trim().replace(/[),.;:!?]+$/g, ""));
}

function normalizeKey(url) {
  const cleaned = cleanUrl(url);

  try {
    const u = new URL(cleaned);
    u.hash = "";
    u.searchParams.sort?.();
    return u.toString().replace(/\/$/, "").toLowerCase();
  } catch {
    return cleaned.replace(/\/$/, "").toLowerCase();
  }
}

function cleanSourceLabel(label) {
  let out = String(label || "").trim();

  out = out.replace(/\s+/g, " ");
  out = out.replace(/\s*\(\s*https?:\/\/[^)]*\)\s*/gi, " ");
  out = out.replace(/\s*\[\s*[^\]]+\s*\]\s*\(\s*https?:\/\/[^)]*\)\s*/gi, " ");
  out = out.replace(/\s*https?:\/\/\S+/gi, " ");
  out = out.replace(/\s+/g, " ").trim();

  out = out.replace(/^[-•*\d.)\[\]\s]+/, "").trim();
  out = out.replace(/[.;,\s]+$/g, "").trim();

  if (!out) return "Source";

  return out;
}

function hostToReadableLabel(url) {
  try {
    const u = new URL(cleanUrl(url));
    const host = u.hostname.replace(/^www\./i, "").toLowerCase();
    const path = u.pathname.toLowerCase();

    const exact = {
      "law.cornell.edu": "Cornell LII",
      "www.law.cornell.edu": "Cornell LII",
      "nycourts.gov": "New York Courts",
      "www.nycourts.gov": "New York Courts",
      "courts.ca.gov": "California Courts",
      "www.courts.ca.gov": "California Courts",
      "leginfo.legislature.ca.gov": "California Legislative Information",
      "law.justia.com": "Justia Law",
      "supreme.justia.com": "Justia U.S. Supreme Court Center",
      "cases.justia.com": "Justia Cases",
      "irs.gov": "IRS",
      "www.irs.gov": "IRS",
      "uscis.gov": "USCIS",
      "www.uscis.gov": "USCIS",
      "hhs.gov": "HHS",
      "www.hhs.gov": "HHS",
      "cms.gov": "CMS",
      "www.cms.gov": "CMS",
      "ecfr.gov": "eCFR",
      "www.ecfr.gov": "eCFR",
      "govinfo.gov": "GovInfo",
      "www.govinfo.gov": "GovInfo",
      "federalregister.gov": "Federal Register",
      "www.federalregister.gov": "Federal Register",
      "public-inspection.federalregister.gov": "Federal Register Public Inspection",
      "congress.gov": "Congress.gov",
      "www.congress.gov": "Congress.gov",
      "sec.gov": "SEC",
      "www.sec.gov": "SEC",
      "fasb.org": "FASB",
      "www.fasb.org": "FASB",
      "gov.ca.gov": "Governor of California",
      "www.gov.ca.gov": "Governor of California",
    };

    if (exact[host]) {
      if (host.includes("irs.gov") && path.includes("instructions")) return "IRS Instructions";
      if (host.includes("uscis.gov") && path.includes("policy-manual")) return "USCIS Policy Manual";
      if (host.includes("hhs.gov") && (path.includes("ocr") || path.includes("hipaa"))) return "HHS OCR";
      return exact[host];
    }

    const parts = host.split(".").filter(Boolean);
    const base = parts.length >= 2 ? parts[parts.length - 2] : parts[0] || "Source";

    return base
      .replace(/[-_]+/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .trim();
  } catch {
    return "Source";
  }
}

function shortenSourceLabel(label) {
  const cleaned = cleanSourceLabel(label);

  const replacements = [
    {
      test: /Cornell\s+LII.*account\s+stated/i,
      value: "Cornell LII — Account stated",
    },
    {
      test: /California\s+Courts.*CACI\s*373/i,
      value: "California Courts — CACI 373",
    },
    {
      test: /Judicial\s+Council\s+of\s+California.*CACI\s*373/i,
      value: "Judicial Council of California — CACI 373",
    },
    {
      test: /Dape\s+Consulting.*Next\s+Trucking/i,
      value: "Dape Consulting Inc. v. Next Trucking Inc.",
    },
    {
      test: /Levy\s+v\.?\s+Prinzmetal/i,
      value: "Levy v. Prinzmetal",
    },
    {
      test: /Aronson\s+Mayefsky.*Praeger/i,
      value: "Aronson Mayefsky & Sloan LLP v. Praeger",
    },
    {
      test: /New\s+York\s+Appellate\s+Digest/i,
      value: "New York Appellate Digest",
    },
    {
      test: /Federal\s+Register.*Premium\s+Processing/i,
      value: "Federal Register — Premium Processing Fees",
    },
    {
      test: /Federal\s+Register.*H-?2B/i,
      value: "Federal Register — H-2B Temporary Worker Program",
    },
    {
      test: /Federal\s+Register.*H\.?R\.?\s*1|USCIS.*Immigration\s+Fees/i,
      value: "Federal Register — USCIS Immigration Fees",
    },
  ];

  for (const item of replacements) {
    if (item.test.test(cleaned)) return item.value;
  }

  if (cleaned.length <= 96) return cleaned;

  return cleaned.slice(0, 93).replace(/\s+\S*$/, "") + "…";
}

function createSourceRegistry() {
  const sources = [];
  const byUrl = new Map();

  function addSource({ url, label, from = "inline" }) {
    const cleanedUrl = cleanUrl(url);
    if (!cleanedUrl) return null;

    const key = normalizeKey(cleanedUrl);

    if (byUrl.has(key)) {
      const existing = byUrl.get(key);

      if (label && (!existing.label || existing.label === hostToReadableLabel(existing.url))) {
        existing.label = shortenSourceLabel(label);
      }

      if (from === "sources") {
        existing.fromSourcesSection = true;
      }

      return existing;
    }

    const source = {
      id: sources.length + 1,
      url: cleanedUrl,
      label: shortenSourceLabel(label || hostToReadableLabel(cleanedUrl)),
      fromSourcesSection: from === "sources",
    };

    sources.push(source);
    byUrl.set(key, source);
    return source;
  }

  function getSources() {
    return sources;
  }

  return { addSource, getSources };
}

function isSourceHeading(line) {
  return SOURCE_SECTION_RE.test(String(line || "").trim());
}

function isStopHeadingAfterSources(line) {
  return SECTION_HEADING_RE.test(String(line || "").trim());
}

function sourceTextHasEvidence(value) {
  const text = String(value || "").trim();
  if (!text) return false;

  MARKDOWN_LINK_RE.lastIndex = 0;
  WRAPPED_MARKDOWN_CITATION_RE.lastIndex = 0;
  BARE_URL_RE.lastIndex = 0;

  const hasMarkdownLink =
    MARKDOWN_LINK_RE.test(text);
  const hasWrappedMarkdownLink =
    WRAPPED_MARKDOWN_CITATION_RE.test(
      text
    );
  const hasBareUrl =
    BARE_URL_RE.test(text);

  MARKDOWN_LINK_RE.lastIndex = 0;
  WRAPPED_MARKDOWN_CITATION_RE.lastIndex = 0;
  BARE_URL_RE.lastIndex = 0;

  return (
    hasMarkdownLink ||
    hasWrappedMarkdownLink ||
    hasBareUrl
  );
}

function splitSourcesSection(rawText) {
  const lines = String(rawText || "").split(/\r?\n/);

  let sourceStart = -1;

  for (let i = 0; i < lines.length; i += 1) {
    if (isSourceHeading(lines[i])) {
      sourceStart = i;
      break;
    }
  }

  if (sourceStart === -1) {
    return {
      bodyText: rawText || "",
      sourceText: "",
      hasSourcesSection: false,
    };
  }

  let sourceEnd = lines.length;

  for (let i = sourceStart + 1; i < lines.length; i += 1) {
    const line = lines[i] || "";

    if (isStopHeadingAfterSources(line)) {
      sourceEnd = i;
      break;
    }
  }

  const sourceText = lines
    .slice(sourceStart + 1, sourceEnd)
    .join("\n")
    .trim();

  // A heading like "Sources" or "References" is not enough by itself.
  // Require actual link/URL evidence before removing it from normal text.
  if (!sourceTextHasEvidence(sourceText)) {
    return {
      bodyText: rawText || "",
      sourceText: "",
      hasSourcesSection: false,
    };
  }

  const before = lines
    .slice(0, sourceStart)
    .join("\n")
    .trim();
  const after = lines
    .slice(sourceEnd)
    .join("\n")
    .trim();

  return {
    bodyText: [before, after]
      .filter(Boolean)
      .join("\n\n"),
    sourceText,
    hasSourcesSection: true,
  };
}

/**
 * Source sections sometimes come through as:
 *
 * - California Courts
 *   (https://courts.ca.gov/...)
 *
 * or:
 *
 * - California Courts ([courts.ca.gov]
 *   (https://courts.ca.gov/...))
 *
 * This helper coalesces continuation lines so extraction is more forgiving.
 */
function coalesceSourceLines(sourceText) {
  const rawLines = String(sourceText || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const out = [];

  for (const rawLine of rawLines) {
    if (!rawLine || isSourceHeading(rawLine)) continue;

    const startsNewSource = /^[-•*]\s+/.test(rawLine) || /^\d+[.)]\s+/.test(rawLine) || /^\[[0-9]+\]\s+/.test(rawLine);

    if (startsNewSource || out.length === 0) {
      out.push(rawLine);
      continue;
    }

    const prev = out[out.length - 1] || "";

    const looksLikeUrlContinuation =
      /^(\(|\[)?https?:\/\//i.test(rawLine) ||
      /^\(https?:\/\//i.test(rawLine) ||
      /^\[[^\]]+\]\s*\(/i.test(rawLine) ||
      prev.includes("[") ||
      prev.includes("(");

    if (looksLikeUrlContinuation) {
      out[out.length - 1] = `${prev} ${rawLine}`;
    } else {
      out.push(rawLine);
    }
  }

  return out;
}

function extractSourcesFromSourceSection(sourceText, registry) {
  const lines = coalesceSourceLines(sourceText);

  for (const rawLine of lines) {
    const line = rawLine.replace(/^[-•*\d.)\[\]\s]+/, "").trim();
    if (!line || isSourceHeading(line)) continue;

    let found = false;

    line.replace(MARKDOWN_LINK_RE, (match, label, url) => {
      const readableLine = line.replace(match, "").trim();
      const finalLabel = readableLine || label || hostToReadableLabel(url);

      registry.addSource({
        url,
        label: finalLabel,
        from: "sources",
      });

      found = true;
      return match;
    });

    if (found) continue;

    const bareMatches = [...line.matchAll(BARE_URL_RE)];

    if (bareMatches.length > 0) {
      for (const m of bareMatches) {
        const url = m[1];
        const label = line.replace(url, "").replace(/[()[\]]/g, "").trim() || hostToReadableLabel(url);

        registry.addSource({
          url,
          label,
          from: "sources",
        });
      }
    }
  }
}

function replaceInlineCitations(bodyText, registry, { isStreaming = false } = {}) {
  let text = String(bodyText || "");

  /*
    Streaming note:
    We still convert completed markdown links during streaming, because otherwise
    raw URLs/markdown links will flash on screen. However, we keep the bottom
    Sources list hidden until streaming is complete.
  */

  // Convert parenthesized markdown citations into citation markers: ([label](url)) -> [1]
  text = text.replace(WRAPPED_MARKDOWN_CITATION_RE, (match, label, url) => {
    const source = registry.addSource({ url, label, from: "inline" });
    return source ? ` [${source.id}]` : "";
  });

  // Convert normal markdown links into label + marker.
  // Example: [Cornell](https://...) -> Cornell [1]
  text = text.replace(MARKDOWN_LINK_RE, (match, label, url) => {
    const source = registry.addSource({ url, label, from: "inline" });
    return source ? `${label} [${source.id}]` : label;
  });

  // Convert bare URLs into citation markers.
  text = text.replace(BARE_URL_RE, (match, url) => {
    const source = registry.addSource({
      url,
      label: hostToReadableLabel(url),
      from: "inline",
    });
    return source ? `[${source.id}]` : "";
  });

  // During streaming, avoid showing a dangling citation marker when the stream
  // is likely still in the middle of creating a link/citation line.
  if (isStreaming) {
    text = text.replace(/\s+\[$/, "");
  }

  // Clean citation spacing.
  text = text.replace(/\s+\[([0-9]+)\]/g, " [$1]");
  text = text.replace(/\s+([.,;:!?])/g, "$1");
  text = text.replace(/\n{3,}/g, "\n\n");

  return text.trim();
}

function isTableSeparator(line) {
  return TABLE_SEPARATOR_RE.test(String(line || ""));
}

function lineLooksLikeTableRow(line) {
  const value = String(line || "").trim();
  if (!value) return false;
  if (!value.includes("|")) return false;

  const withoutEdgePipes = value.replace(/^\|/, "").replace(/\|$/, "");
  return withoutEdgePipes.split("|").length >= 2;
}

function isMarkdownTableBlock(lines, { isStreaming = false } = {}) {
  if (!Array.isArray(lines) || lines.length < 2) {
    return false;
  }

  const cleanLines = lines
    .map((line) => String(line || "").trim())
    .filter(Boolean);

  const separatorIndex =
    cleanLines.findIndex(isTableSeparator);

  if (separatorIndex > 0) {
    const headerLine =
      cleanLines[separatorIndex - 1] || "";

    if (!lineLooksLikeTableRow(headerLine)) {
      return false;
    }

    const headerCols =
      getPipeColumnCount(headerLine);

    if (headerCols < 2) {
      return false;
    }

    const dataRows = cleanLines
      .slice(separatorIndex + 1)
      .filter((line) =>
        lineLooksLikeTableRow(line)
      );

    // Do not build an empty table card from only a header/separator.
    return dataRows.some(
      (line) =>
        getPipeColumnCount(line) >= 2
    );
  }

  // Preserve simple model-generated pipe tables without markdown separators,
  // but require 3 consistent rows to avoid accidental prose conversion.
  return looksLikeStrongSimplePipeTable(
    cleanLines
  );
}


function getPipeColumnCount(line) {
  const value = String(line || "").trim();
  if (!value || !value.includes("|")) return 0;

  return value
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim())
    .length;
}

function looksLikeStrongSimplePipeTable(lines) {
  const rows = (Array.isArray(lines) ? lines : [])
    .map((line) => String(line || "").trim())
    .filter(Boolean)
    .filter((line) => lineLooksLikeTableRow(line));

  if (rows.length < 3) return false;

  const counts = rows.map(getPipeColumnCount);
  const firstCount = counts[0];

  if (firstCount < 2) return false;

  return counts.every(
    (count) => count === firstCount
  );
}

function looksLikeTablePayload(value) {
  const lines = String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return false;

  const separatorIndex =
    lines.findIndex(isTableSeparator);

  if (separatorIndex > 0) {
    const header =
      lines[separatorIndex - 1] || "";
    const headerCols =
      getPipeColumnCount(header);

    if (
      headerCols < 2 ||
      !lineLooksLikeTableRow(header)
    ) {
      return false;
    }

    const dataRows = lines
      .slice(separatorIndex + 1)
      .filter((line) =>
        lineLooksLikeTableRow(line)
      );

    return dataRows.some(
      (line) =>
        getPipeColumnCount(line) >= 2
    );
  }

  // Preserve simple model-generated tables without a markdown separator, but
  // require 3 consistent rows so ordinary prose with stray pipes stays text.
  return looksLikeStrongSimplePipeTable(lines);
}

const VISUAL_DATA_KEY_RE =
  /(?:^|[;\n])\s*(?:data|dataset|labels?|categories?|values?|series|datasets|points?|x|y|x_values|y_values|rows?|cols?|columns?|tasks?|events?|timeline|candles?|ohlc|mean|mu|sd|std|sigma|low|lower|high|upper|expected|base|mid|bandwidth|bins?|percent|progress|score)\s*[:=]\s*[^\n;]+/i;

const VISUAL_TYPE_KEY_RE =
  /(?:^|[;\n])\s*(?:type|chart|graph|visual|diagram)\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

const VISUAL_NUMERIC_SIGNAL_RE =
  /(?:^|[=,:|\s])[-+]?\$?\d+(?:,\d{3})*(?:\.\d+)?%?(?=$|[,\s;|])/;

const VISUAL_PAIR_SIGNAL_RE =
  /(?:^|[;|\n])\s*[^:=;\n|]{1,48}\s*[:=]\s*[-+]?\$?\d+(?:\.\d+)?%?(?:\s*,\s*[-+]?\$?\d+(?:\.\d+)?%?){0,3}(?=$|[;|\n])/;

const VISUAL_DATE_SIGNAL_RE =
  /\b(?:\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Sept|Oct|Nov|Dec)[a-z]*\s+\d{1,2}(?:,\s*\d{4})?)\b/i;

const VISUAL_EQUATION_SIGNAL_RE =
  /\by\s*=\s*[^\n;]+/i;

function normalizeVisualTypeToken(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function hasKnownVisualTypeToken(value) {
  const type =
    normalizeVisualTypeToken(value);

  return (
    DATA_VISUAL_MARKER_TYPES.has(type) ||
    GEOMETRY_VISUAL_TYPES.has(type) ||
    [
      "horizontal_bar",
      "vertical_bar",
      "trend",
      "multi_line",
      "scatterplot",
      "scatter_plot",
      "distribution",
      "normal_curve",
      "gaussian",
      "density",
      "kde",
      "roc",
      "pr_curve",
      "spider",
      "bridge",
      "box_whisker",
      "heat_map",
      "matrix",
      "meter",
      "gauge_chart",
      "progress_bar",
      "gantt_chart",
      "ohlc",
      "coordinate",
      "sine",
      "cosine",
    ].includes(type)
  );
}

function looksLikeGeometryPayload(markerType, content) {
  const text = String(content || "").trim();
  const marker =
    normalizeVisualTypeToken(markerType);

  if (!text) return false;

  const explicitType =
    text.match(VISUAL_TYPE_KEY_RE)?.[1] || "";

  if (
    explicitType &&
    GEOMETRY_VISUAL_TYPES.has(
      normalizeVisualTypeToken(explicitType)
    )
  ) {
    return true;
  }

  if (GEOMETRY_VISUAL_TYPES.has(marker)) {
    // A direct [triangle], [venn], [circle], etc. marker is a strong
    // presentation instruction, but still require some payload so a stray
    // empty marker cannot manufacture a visual.
    return text.length > 0;
  }

  const lower = text.toLowerCase();

  const hasGeometryWord = [
    "circle",
    "ellipse",
    "oval",
    "square",
    "rectangle",
    "triangle",
    "right triangle",
    "right_triangle",
    "angle",
    "parabola",
    "coordinate graph",
    "coordinate_graph",
    "coordinate plane",
    "number line",
    "number_line",
    "venn",
    "flowchart",
    "flow chart",
    "sine",
    "cosine",
  ].some((token) =>
    lower.includes(token)
  );

  if (hasGeometryWord) {
    return true;
  }

  if (
    marker === "graph" ||
    marker === "plot" ||
    marker === "curve"
  ) {
    return VISUAL_EQUATION_SIGNAL_RE.test(
      text
    );
  }

  return false;
}

function looksLikeVisualPayload(markerType, value) {
  const marker =
    normalizeVisualTypeToken(markerType);
  const text = String(value || "").trim();

  if (!text) return false;

  // Filenames, paths, and URLs are not visual data.
  if (
    ORDINARY_FILE_EXTENSION_RE.test(text) ||
    /https?:\/\//i.test(text) ||
    WINDOWS_OR_UNIX_PATH_RE.test(text)
  ) {
    return false;
  }

  const explicitType =
    text.match(VISUAL_TYPE_KEY_RE)?.[1] || "";

  const explicitKnownType =
    explicitType &&
    hasKnownVisualTypeToken(explicitType);

  if (
    GEOMETRY_VISUAL_TYPES.has(marker)
  ) {
    return looksLikeGeometryPayload(
      marker,
      text
    );
  }

  if (
    GENERIC_VISUAL_MARKER_TYPES.has(marker)
  ) {
    if (explicitKnownType) {
      const explicitNormalized =
        normalizeVisualTypeToken(
          explicitType
        );

      if (
        GEOMETRY_VISUAL_TYPES.has(
          explicitNormalized
        )
      ) {
        return true;
      }

      return (
        VISUAL_DATA_KEY_RE.test(text) &&
        (
          VISUAL_NUMERIC_SIGNAL_RE.test(
            text
          ) ||
          VISUAL_PAIR_SIGNAL_RE.test(
            text
          ) ||
          (
            explicitNormalized === "timeline" &&
            VISUAL_DATE_SIGNAL_RE.test(text)
          )
        )
      );
    }

    return (
      looksLikeGeometryPayload(
        marker,
        text
      ) ||
      (
        VISUAL_DATA_KEY_RE.test(text) &&
        (
          VISUAL_NUMERIC_SIGNAL_RE.test(
            text
          ) ||
          VISUAL_PAIR_SIGNAL_RE.test(
            text
          )
        )
      )
    );
  }

  if (DATA_VISUAL_MARKER_TYPES.has(marker)) {
    if (marker === "timeline") {
      return (
        /(?:^|[;\n])\s*(?:events?|timeline|data)\s*[:=]/i.test(
          text
        ) &&
        VISUAL_DATE_SIGNAL_RE.test(text)
      );
    }

    return (
      VISUAL_DATA_KEY_RE.test(text) &&
      (
        VISUAL_NUMERIC_SIGNAL_RE.test(
          text
        ) ||
        VISUAL_PAIR_SIGNAL_RE.test(
          text
        )
      )
    );
  }

  return false;
}

function getSpecialBlockMatch(line) {
  const match = String(line || "").match(
    SPECIAL_BLOCK_RE
  );
  if (!match) return null;

  const rawType = String(
    match[1] || ""
  ).toLowerCase();
  const content = String(
    match[2] || ""
  ).trim();

  const type =
    resolveSpecialBlockType(
      rawType,
      content
    ) || "text";

  const hasExplicitChartType =
    /(?:^|[;\s])type\s*[:=]/i.test(
      content
    );

  let finalContent = content;

  if (
    type === "chart" &&
    rawType !== "chart" &&
    !GENERIC_VISUAL_MARKER_TYPES.has(
      rawType
    ) &&
    !hasExplicitChartType
  ) {
    const normalizedMarker =
      rawType === "flowchart"
        ? "flowchart_basic"
        : rawType;

    finalContent =
      `type=${normalizedMarker}; ${content}`.trim();
  }

  return {
    type,
    rawType,
    content: finalContent,
  };
}

function collectContinuationLines(lines, startIndex, { isStreaming = false } = {}) {
  /*
    Handles rare output like:
      [math]
      ROI = Net Profit / Investment

    or:
      [chart]
      type=bar; labels=...
  */
  const collected = [];
  let i = startIndex;

  while (i < lines.length) {
    const line = lines[i] || "";
    const trimmed = line.trim();

    if (!trimmed) break;
    if (getSpecialBlockMatch(line)) break;
    if (isMarkdownTableBlock(lines.slice(i, Math.min(lines.length, i + 6)), { isStreaming })) break;

    collected.push(line);
    i += 1;
  }

  return {
    content: collected.join("\n").trim(),
    nextIndex: i,
  };
}

function splitIntoRenderableBlocks(text, { isStreaming = false } = {}) {
  const lines = String(text || "").split(/\r?\n/);
  const blocks = [];
  let buffer = [];
  let tableBuffer = [];
  let collectingTable = false;

  function flushText() {
    const content = buffer.join("\n").trim();
    if (content) {
      blocks.push({ type: "text", content });
    }
    buffer = [];
  }

  function flushTable() {
    const content = tableBuffer.join("\n").trim();

    if (content) {
      blocks.push({ type: "table", content });
    }

    tableBuffer = [];
    collectingTable = false;
  }

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    /*
      Final messages:
      Detect raw LaTeX display blocks before plain text/table parsing.
      This catches:
        \\[
        x=\\frac{...}{...}
        \\]
      and one-line forms like \\[x=...\\] or $$x=...$$.
    */
    if (!isStreaming) {
      const singleMath = getSingleLineMathBlock(line);
      if (singleMath) {
        if (collectingTable) flushTable();
        flushText();
        blocks.push(singleMath);
        continue;
      }

      if (DISPLAY_MATH_OPEN_RE.test(line)) {
        if (collectingTable) flushTable();
        flushText();
        const mathBlock = collectDisplayMathBlock(lines, i);
        if (mathBlock.content) {
          blocks.push({ type: "math", content: mathBlock.content });
        }
        i = Math.max(i, mathBlock.nextIndex - 1);
        continue;
      }

      // If a formula appears as its own paragraph without an explicit [math]
      // marker, render it as math instead of raw text. Keep this conservative
      // so ordinary prose/list lines do not become math cards.
      const nextLine = lines[i + 1] || "";
      const prevLine = lines[i - 1] || "";
      const isolated = !String(prevLine).trim() && !String(nextLine).trim();
      if (isolated && looksLikeStandaloneMathLine(line)) {
        if (collectingTable) flushTable();
        flushText();
        blocks.push({ type: "math", content: stripMathFenceLine(line) });
        continue;
      }
    }

    /*
      Final messages:
      Detect [math], [chart], [graph], and [geometry] line-by-line.
      This is the main fix for mixed answers.
    */
    const special = !isStreaming ? getSpecialBlockMatch(line) : null;

    if (special) {
      if (collectingTable) flushTable();
      flushText();

      let content = special.content;

      if (!content) {
        const continuation = collectContinuationLines(lines, i + 1, { isStreaming });
        content = continuation.content;
        i = Math.max(i, continuation.nextIndex - 1);
      }

      if (content) {
        const resolvedType =
          resolveSpecialBlockType(
            special.rawType,
            content
          ) || "text";

        let resolvedContent = content;

        if (
          resolvedType === "chart" &&
          special.rawType !== "chart" &&
          !GENERIC_VISUAL_MARKER_TYPES.has(
            special.rawType
          ) &&
          !/(?:^|[;\s])type\s*[:=]/i.test(
            content
          )
        ) {
          const normalizedMarker =
            special.rawType === "flowchart"
              ? "flowchart_basic"
              : special.rawType;

          resolvedContent =
            `type=${normalizedMarker}; ${content}`.trim();
        }

        blocks.push({
          type: resolvedType,
          content: resolvedContent,
        });
      }

      continue;
    }

    /*
      Look ahead farther than the old 3-line window so tables are still detected
      if there is a short caption/blank line or slightly uneven streaming output.
    */
    const currentWindow = lines.slice(i, Math.min(lines.length, i + 6));

    if (!collectingTable && isMarkdownTableBlock(currentWindow, { isStreaming })) {
      flushText();
      collectingTable = true;
      tableBuffer.push(line);
      continue;
    }

    if (collectingTable) {
      if (lineLooksLikeTableRow(line) || isTableSeparator(line) || /^\s*$/.test(line)) {
        tableBuffer.push(line);
        continue;
      }

      flushTable();
      buffer.push(line);
      continue;
    }

    buffer.push(line);
  }

  if (collectingTable) {
    if (isStreaming) {
      /*
        If streaming is still producing the table, keep it as text until stable.
        The final saved message will render it as a real table.
      */
      buffer.push(...tableBuffer);
    } else {
      flushTable();
    }
  }

  flushText();

  return blocks;
}

function detectSpecialBlock(block, { isStreaming = false } = {}) {
  const text = String(block?.content || "").trim();

  if (!text) return block;
  if (isStreaming) return block;

  /*
    Backward compatibility:
    If an entire block still starts with a marker, convert it. The main splitter
    already handles normal mixed marker lines.
  */
  const special = getSpecialBlockMatch(text);
  if (!special) return block;

  let content = special.content;

  if (!content) {
    content = text.split(/\r?\n/).slice(1).join("\n").trim();
  }

  if (!content) return block;

  const resolvedType =
    resolveSpecialBlockType(
      special.rawType,
      content
    ) || "text";

  let resolvedContent = content;

  if (
    resolvedType === "chart" &&
    special.rawType !== "chart" &&
    !GENERIC_VISUAL_MARKER_TYPES.has(
      special.rawType
    ) &&
    !/(?:^|[;\s])type\s*[:=]/i.test(
      content
    )
  ) {
    const normalizedMarker =
      special.rawType === "flowchart"
        ? "flowchart_basic"
        : special.rawType;

    resolvedContent =
      `type=${normalizedMarker}; ${content}`.trim();
  }

  return {
    type: resolvedType,
    content: resolvedContent,
  };
}


function groupConsecutiveMathBlocks(blocks) {
  /*
    Consecutive math blocks should render as one formula card:
      [math] A = ...
      [math] B = ...
      [math] C = ...

    But do NOT group across charts, tables, or text:
      [math] A = ...
      [chart] ...
      [math] B = ...

    This preserves visual ordering and prevents charts from mismatching the
    formulas/calculations they belong to.
  */
  const grouped = [];
  let mathBuffer = [];

  function flushMathBuffer() {
    if (!mathBuffer.length) return;

    const content = mathBuffer
      .map((block) => String(block.content || "").trim())
      .filter(Boolean)
      .join("\n");

    if (content) {
      grouped.push({
        type: "math",
        content,
      });
    }

    mathBuffer = [];
  }

  for (const block of blocks) {
    if (block?.type === "math") {
      mathBuffer.push(block);
      continue;
    }

    flushMathBuffer();

    if (block && String(block.content || "").trim()) {
      grouped.push(block);
    }
  }

  flushMathBuffer();

  return grouped;
}

function buildAnswerModel(rawText, { isStreaming = false } = {}) {
  const registry = createSourceRegistry();

  const { bodyText, sourceText, hasSourcesSection } = splitSourcesSection(rawText);

  /*
    Final message:
    - Extract source lines first, so bottom sources control labels/numbering.
    - Then inline links reuse the same numbers.

    Streaming message:
    - Do NOT render the source list yet.
    - Still remove/convert inline links so raw URLs do not flash.
  */
  if (!isStreaming && hasSourcesSection) {
    extractSourcesFromSourceSection(sourceText, registry);
  }

  const bodyWithCitationMarkers = replaceInlineCitations(bodyText, registry, { isStreaming });

  const splitBlocks = splitIntoRenderableBlocks(bodyWithCitationMarkers, { isStreaming }).map((block) =>
    detectSpecialBlock(block, { isStreaming })
  );

  /*
    Final saved messages should group consecutive formulas/calculations into
    one math card. Streaming keeps blocks ungrouped for safer live rendering.
  */
  const blocks = isStreaming ? splitBlocks : groupConsecutiveMathBlocks(splitBlocks);

  return {
    blocks,
    sources: isStreaming ? [] : registry.getSources(),
  };
}

export default function AnswerRenderer({ text, isStreaming = false }) {
  useAnswerRendererStyles();

  const cleanText = String(text || "");

  const model = useMemo(() => buildAnswerModel(cleanText, { isStreaming }), [cleanText, isStreaming]);

  if (!cleanText.trim()) return null;

  return (
    <div className={`answer-renderer${isStreaming ? " answer-renderer-streaming" : ""}`}>
      {model.blocks.map((block, index) => {
        if (block.type === "table") {
          return (
            <div className="answer-table-region" key={`table-${index}`}>
              <AnswerTable value={block.content} />
            </div>
          );
        }

        if (block.type === "math") {
          return <AnswerMath key={`math-${index}`} value={block.content} />;
        }

        if (block.type === "chart") {
          return <AnswerChart key={`chart-${index}`} value={block.content} />;
        }

        return <AnswerText key={`text-${index}`} value={block.content} />;
      })}

      {!isStreaming && model.sources.length > 0 && <AnswerSources sources={model.sources} batchSize={5} />}
    </div>
  );
}
